import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { CARNET } from "@/constants/testIds";
import {
  Plus, Trash2, Wine, Sparkles, Loader2, Globe, Tag,
  PenLine, BookOpen, Search, Lock, Info, Camera,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import AiBadge from "@/components/AiBadge";
import DemoLoop from "@/components/DemoLoop";
import TastingDetailModal from "@/components/TastingDetailModal";
import TastingDraftCard from "@/components/TastingDraftCard";
import LabelPhotoPicker from "@/components/LabelPhotoPicker";
import { isGuest, getRemaining, consume } from "@/lib/guest";

const TYPES = ["Vin", "Whisky", "Rhum", "Gin", "Cognac", "Bière"];

const DEMO_STEPS = [
  { title: "1. Notez en langage libre", body: "« Goûté ce Chablis 2020 Raveneau, paille, citron vert, finale longue, 17/20 »", icon: PenLine, color: "text-[#1F3A5F]" },
  { title: "2. geoVINUM AI structure la fiche", body: "Cuvée · millésime · robe · nez · bouche · note", icon: Sparkles, color: "text-[#E5276A]" },
  { title: "3. Enrichissement web", body: "Photo bouteille, site du domaine, prix indicatif", icon: Search, color: "text-[#1F3A5F]" },
  { title: "4. Sauvegardé dans le carnet", body: "Retrouvez tout en 1 clic, recherche libre, export PDF.", icon: BookOpen, color: "text-[#E5276A]" },
];

export default function Carnet() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("ai");
  const [showForm, setShowForm] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPreview, setAiPreview] = useState(null); // guest preview only
  const [guestRemaining, setGuestRemaining] = useState(getRemaining("tasting"));
  const [form, setForm] = useState({ product_type: "Vin", name: "", photo_url: "", score: "", robe: "", nez: "", bouche: "", comment: "" });
  const [detail, setDetail] = useState(null); // currently opened tasting
  const [photoDraft, setPhotoDraft] = useState(null);

  const guest = isGuest();

  useEffect(() => {
    if (user) {
      api.get("/tastings")
        .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
        .catch(() => setItems([]));
    } else {
      setGuestRemaining(getRemaining("tasting"));
    }
  }, [user]);

  // Deep-link support — `/carnet?edit={id}` opens the tasting straight into
  // the edit modal (used by the "Déguster" button on the Vision result page).
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || !items.length) return;
    const match = items.find((x) => x.id === editId);
    if (match) {
      setDetail(match);
      // Clean the URL so the modal doesn't reopen after the user closes it.
      setSearchParams({}, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const submitAi = async (e) => {
    e.preventDefault();
    if (!aiNotes.trim()) { toast.error("Notez votre dégustation en quelques mots"); return; }
    if (guest && guestRemaining <= 0) {
      toast.error("Limite atteinte. Créez un compte gratuit pour continuer.");
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post("/tastings/ai-create", { notes: aiNotes, lang });
      // Debit quota only after a successful API response (no rollback needed)
      if (guest) {
        const c = consume("tasting");
        setGuestRemaining(c.remaining);
      }
      if (data.is_guest_preview) {
        setAiPreview(data);
        toast.success("Aperçu généré — créez un compte pour sauvegarder.");
      } else {
        setItems((arr) => [data, ...arr]);
        setShowForm(false);
        setAiNotes("");
        toast.success("Dégustation structurée par geoVINUM AI");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Erreur IA");
    } finally { setAiBusy(false); }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login?next=/carnet"); return; }
    if (!form.name.trim()) { toast.error(t("carnet.toast.nameRequired")); return; }
    const payload = { ...form, score: form.score ? parseFloat(form.score) : null };
    const { data } = await api.post("/tastings", payload);
    setItems((arr) => [data, ...arr]);
    setShowForm(false);
    setForm({ product_type: "Vin", name: "", photo_url: "", score: "", robe: "", nez: "", bouche: "", comment: "" });
    toast.success(t("carnet.toast.saved"));
  };

  const del = async (id) => {
    await api.delete(`/tastings/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    toast(t("carnet.toast.deleted"));
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5">{t("carnet.kicker")} <AiBadge variant="icon" /></span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05] inline-flex items-center gap-3 flex-wrap">
            {t("carnet.title")} <AiBadge />
          </h1>
          <p className="text-[#4A5568] mt-3 max-w-2xl">Notez en langage naturel — geoVINUM AI structure votre fiche, trouve la photo, le domaine et un prix indicatif.</p>
        </div>
        <button
          data-testid={CARNET.newBtn}
          onClick={() => { setShowForm((v) => !v); setAiPreview(null); }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4" /> {t("carnet.new")}
        </button>
      </div>

      {/* Animated demo (always visible at the top) */}
      <div className="mt-6">
        <DemoLoop steps={DEMO_STEPS} testId="carnet-demo" />
      </div>

      {/* Guest banner */}
      {guest && (
        <div className="mt-5 rounded-2xl border border-[#E5276A]/25 bg-[#FFF8FB] p-4 flex items-start gap-3" data-testid="carnet-guest-banner">
          <Info className="w-4 h-4 text-[#E5276A] shrink-0 mt-0.5" />
          <div className="text-sm text-[#1F3A5F] flex-1 min-w-0">
            <b>Mode invité</b> · Vous avez <b>{guestRemaining}</b> essai{guestRemaining > 1 ? "s" : ""} IA restant{guestRemaining > 1 ? "s" : ""} aujourd'hui.
            <Link to="/register?next=/carnet" className="ml-2 underline font-bold text-[#E5276A]" data-testid="carnet-guest-register">Créer un compte gratuit</Link> pour sauvegarder et continuer.
          </div>
        </div>
      )}

      {showForm && (
        <div className="mt-8">
          <div className="inline-flex rounded-lg border border-[#1F3A5F]/15 overflow-hidden bg-white mb-4">
            <button onClick={() => setMode("ai")} data-testid="carnet-mode-ai" className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${mode === "ai" ? "bg-[#E5276A] text-white" : "text-[#1F3A5F]"}`}>
              <Sparkles className="w-3.5 h-3.5" /> Mode IA
            </button>
            <button onClick={() => setMode("photo")} data-testid="carnet-mode-photo" className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${mode === "photo" ? "bg-[#E5276A] text-white" : "text-[#1F3A5F]"}`}>
              <Camera className="w-3.5 h-3.5" /> Photo
            </button>
            <button onClick={() => setMode("manual")} data-testid="carnet-mode-manual" className={`px-4 py-2 text-sm font-bold ${mode === "manual" ? "bg-[#E5276A] text-white" : "text-[#1F3A5F]"}`}>
              Manuel{guest ? " 🔒" : ""}
            </button>
          </div>

          {mode === "photo" ? (
            <PhotoMode lang={lang} guest={guest} guestRemaining={guestRemaining} draft={photoDraft}
                       onDraft={(d) => { setPhotoDraft(d); if (guest) setGuestRemaining(consume("tasting").remaining); }}
                       onSaved={(d) => setItems((arr) => [d, ...arr])} />
          ) : mode === "ai" ? (
            <form onSubmit={submitAi} className="p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold inline-flex items-center gap-1.5">
                  Vos notes de dégustation <AiBadge variant="icon" />
                </span>
                <textarea data-testid="carnet-ai-notes" rows={6} value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} className={inputCls + " mt-2"}
                  placeholder="Goûté ce soir un Chablis 2020 du domaine Raveneau, robe paille brillante, nez de pierre à fusil et citron vert, bouche tendue, finale longue, 17/20" />
              </label>
              <div className="mt-4 flex justify-end gap-3 flex-wrap">
                <button type="button" onClick={() => { setShowForm(false); setAiPreview(null); }} className="px-5 py-2.5 text-sm text-[#4A5568]">Annuler</button>
                <button data-testid="carnet-ai-submit" disabled={aiBusy || (guest && guestRemaining <= 0)} className="btn-primary disabled:opacity-50">
                  {aiBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Structuration…</> : <><Sparkles className="w-4 h-4" /> {guest ? "Tester (sans compte)" : "Structurer la fiche"}</>}
                </button>
              </div>
              {guest && guestRemaining <= 0 && (
                <p className="mt-3 text-xs text-[#E5276A] font-bold">Quota gratuit épuisé. <Link to="/register?next=/carnet" className="underline">Créer un compte</Link> pour continuer.</p>
              )}

              {/* Guest preview */}
              {aiPreview && (
                <div className="mt-6 rounded-xl border-2 border-[#E5276A]/40 bg-gradient-to-br from-[#FCE4ED]/40 to-white p-5" data-testid="carnet-ai-preview">
                  <div className="flex items-center gap-2 mb-2">
                    <AiBadge label="Aperçu IA" />
                    <span className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold">Non sauvegardé</span>
                  </div>
                  <div className="font-display text-xl text-[#1F3A5F]">{aiPreview.name}{aiPreview.year ? ` · ${aiPreview.year}` : ""}</div>
                  {aiPreview.producer && <div className="text-sm text-[#8B95A1]">{aiPreview.producer} · {aiPreview.region || "—"}</div>}
                  <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
                    {aiPreview.robe && <div><b className="text-[#E5276A]">Robe</b><br />{aiPreview.robe}</div>}
                    {aiPreview.nez && <div><b className="text-[#E5276A]">Nez</b><br />{aiPreview.nez}</div>}
                    {aiPreview.bouche && <div><b className="text-[#E5276A]">Bouche</b><br />{aiPreview.bouche}</div>}
                  </div>
                  {aiPreview.score != null && <div className="mt-3 font-display text-2xl text-[#E5276A]">{aiPreview.score}<span className="text-sm text-[#8B95A1]">/20</span></div>}
                  <Link to="/register?next=/carnet" data-testid="carnet-preview-register" className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                    Sauvegarder dans mon carnet (gratuit)
                  </Link>
                </div>
              )}
            </form>
          ) : (
            <form data-testid={CARNET.form} onSubmit={submitManual} className="grid md:grid-cols-2 gap-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
              {guest && (
                <div className="md:col-span-2 rounded-xl bg-[#FBF7F3] p-4 text-sm text-[#4A5568] inline-flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#E5276A]" /> Mode manuel réservé aux comptes — <Link to="/register?next=/carnet" className="underline font-bold text-[#E5276A]">créez le vôtre</Link>.
                </div>
              )}
              <Field label={t("carnet.f.type")}><select data-testid={CARNET.fieldType} value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} className={inputCls}>{TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</select></Field>
              <Field label={t("carnet.f.name")}><input data-testid={CARNET.fieldName} className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label={t("carnet.f.score")}><input data-testid={CARNET.fieldScore} type="number" min="0" max="20" step="0.5" className={inputCls} value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} /></Field>
              <Field label={t("carnet.f.photo")}><input className={inputCls} value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></Field>
              <Field label={t("carnet.f.robe")}><input data-testid={CARNET.fieldRobe} className={inputCls} value={form.robe} onChange={(e) => setForm({ ...form, robe: e.target.value })} /></Field>
              <Field label={t("carnet.f.nez")}><input data-testid={CARNET.fieldNez} className={inputCls} value={form.nez} onChange={(e) => setForm({ ...form, nez: e.target.value })} /></Field>
              <Field label={t("carnet.f.bouche")} full><input data-testid={CARNET.fieldBouche} className={inputCls} value={form.bouche} onChange={(e) => setForm({ ...form, bouche: e.target.value })} /></Field>
              <Field label={t("carnet.f.comment")} full><textarea data-testid={CARNET.fieldComment} rows={3} className={inputCls} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></Field>
              <div className="md:col-span-2 flex justify-end gap-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 text-sm text-[#4A5568]">{t("carnet.cancel")}</button>
                <button data-testid={CARNET.submit} type="submit" disabled={guest} className="btn-primary disabled:opacity-50">{t("carnet.save")}</button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* List */}
      <div className="mt-10 space-y-3">
        {!guest && items.length === 0 && (
          <div className="text-center py-20 text-[#8B95A1]"><Wine className="w-8 h-8 mx-auto text-[#E5276A] mb-3" />{t("carnet.empty")}</div>
        )}
        {guest && (
          <div className="text-center py-12 rounded-2xl border border-dashed border-[#1F3A5F]/15">
            <Wine className="w-8 h-8 mx-auto text-[#E5276A] mb-3 opacity-60" />
            <p className="text-sm text-[#4A5568]">Vos dégustations apparaîtront ici une fois votre compte créé.</p>
            <Link to="/register?next=/carnet" className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#E5276A] underline">Créer mon compte gratuit</Link>
          </div>
        )}
        {items.map((tt) => (
          <button
            key={tt.id}
            type="button"
            onClick={() => setDetail(tt)}
            data-testid={CARNET.item(tt.id)}
            className="app-card w-full text-left flex items-center gap-5 p-4 hover:border-[#E5276A]/40 hover:shadow-md transition-all cursor-pointer"
          >
            {tt.photo_url ? <img src={tt.photo_url} alt="" className="w-16 h-20 rounded-lg object-cover" /> : (
              <div className="w-16 h-20 rounded-lg bg-[#FBF7F3] flex items-center justify-center"><Wine className="w-5 h-5 text-[#E5276A]" /></div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {tt.product_type}{tt.year ? ` · ${tt.year}` : ""}</div>
              <div className="font-display text-lg text-[#1F3A5F] truncate">{tt.name}{tt.producer ? <span className="text-[#8B95A1] font-normal text-sm"> — {tt.producer}</span> : null}</div>
              <div className="text-xs text-[#8B95A1] truncate">{[tt.robe, tt.nez, tt.bouche].filter(Boolean).join(" · ") || "—"}</div>
              <div className="text-xs text-[#8B95A1] mt-1 flex items-center gap-3 flex-wrap">
                {tt.domain_url && <span onClick={(e) => { e.stopPropagation(); window.open(tt.domain_url, "_blank"); }} className="inline-flex items-center gap-1 text-[#E5276A] hover:underline cursor-pointer"><Globe className="w-3 h-3" /> Site domaine</span>}
                {tt.price_indication && <span>Prix indic. {tt.price_indication}</span>}
              </div>
            </div>
            {tt.score != null && (
              <div className="text-right shrink-0">
                <div className="font-display text-2xl text-[#E5276A]">{tt.score}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#8B95A1]">/20</div>
              </div>
            )}
            <span
              role="button"
              tabIndex={0}
              data-testid={CARNET.delete(tt.id)}
              onClick={(e) => { e.stopPropagation(); del(tt.id); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); del(tt.id); } }}
              className="p-2 rounded-full text-[#8B95A1] hover:text-[#E5276A] hover:bg-[#FBF7F3] cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
            </span>
          </button>
        ))}
      </div>

      {detail && (
        <TastingDetailModal
          tasting={detail}
          onClose={() => setDetail(null)}
          onDelete={del}
          onUpdate={(updated) => {
            setItems((arr) => arr.map((x) => (x.id === updated.id ? updated : x)));
            setDetail(updated);
          }}
        />
      )}
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]";
const Field = ({ label, children, full }) => (
  <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{label}</span>
    {children}
  </label>
);

const PHOTO_TXT = {
  fr: { title: "Photographiez l'étiquette", body: "geoVINUM AI lit la cuvée, le domaine, le millésime et l'appellation, trouve une belle photo de la bouteille et vous suggère un profil robe / nez / bouche à ajuster.", quota: "Quota gratuit épuisé." },
  en: { title: "Snap the label", body: "geoVINUM AI reads the cuvée, estate, vintage and appellation, finds a quality bottle photo and suggests a colour / nose / palate profile for you to adjust.", quota: "Free quota reached." },
  es: { title: "Fotografía la etiqueta", body: "geoVINUM IA lee la cuvée, la bodega, la añada y la denominación, encuentra una buena foto de la botella y te sugiere un perfil color / nariz / boca para ajustar.", quota: "Cuota gratuita agotada." },
  pt: { title: "Fotografa o rótulo", body: "A geoVINUM IA lê a cuvée, o produtor, a colheita e a denominação, encontra uma boa foto da garrafa e sugere um perfil cor / nariz / boca para ajustares.", quota: "Quota gratuita esgotada." },
};

function PhotoMode({ lang, guest, guestRemaining, draft, onDraft, onSaved }) {
  const T = PHOTO_TXT[lang] || PHOTO_TXT.fr;
  const blocked = guest && guestRemaining <= 0;
  return (
    <div className="p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm space-y-5" data-testid="carnet-photo-mode">
      <div>
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold inline-flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" /> {T.title} <AiBadge variant="icon" />
        </span>
        <p className="text-sm text-[#4A5568] mt-2 max-w-2xl">{T.body}</p>
      </div>
      {blocked
        ? <p className="text-xs text-[#E5276A] font-bold">{T.quota} <Link to="/register?next=/carnet" className="underline">Créer un compte</Link></p>
        : <LabelPhotoPicker lang={lang} onResult={(d) => onDraft({ ...d, _k: Date.now() })} testIdPrefix="carnet-photo" />}
      {draft && <div className="max-w-xl"><TastingDraftCard key={draft._k} draft={draft} lang={lang} onSaved={onSaved} /></div>}
    </div>
  );
}
